import type {
  Service,
  PortfolioItem,
  Testimonial,
  TeamMember,
  BlogPost,
  Stat,
  ContactInfo,
  Client,
} from '../types'

import { services as staticServices } from '../data/services'
import { portfolioItems as staticPortfolio } from '../data/portfolio'
import { testimonials as staticTestimonials } from '../data/testimonials'
import { teamMembers as staticTeam } from '../data/team'
import { blogPosts as staticBlogPosts } from '../data/blog'
import { stats as staticStats } from '../data/stats'
import { clients as staticClients } from '../data/clients'
import { contactInfo as staticContact } from '../data/contact'

const API = import.meta.env.VITE_API_URL || '/api'

function toCamel<T>(obj: any): T {
  if (Array.isArray(obj)) return obj.map(toCamel) as any
  if (obj !== null && typeof obj === 'object') {
    const out: any = {}
    for (const [k, v] of Object.entries(obj)) {
      const camel = k.replace(/_([a-z])/g, (_, l) => l.toUpperCase())
      out[camel] = toCamel(v)
    }
    return out
  }
  return obj
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return toCamel<T>(await res.json())
}

export async function getServices(): Promise<Service[]> {
  try {
    return await fetchJson(`${API}/services`)
  } catch {
    return staticServices
  }
}

export async function getServiceById(id: string): Promise<Service | undefined> {
  const items = await getServices()
  return items.find((s) => s.id === id)
}

export async function getPortfolioItems(
  category?: PortfolioItem['category'] | 'all'
): Promise<PortfolioItem[]> {
  try {
    const params = category && category !== 'all' ? `?category=${category}` : ''
    return await fetchJson(`${API}/portfolio${params}`)
  } catch {
    if (category && category !== 'all') {
      return staticPortfolio.filter((item) => item.category === category)
    }
    return staticPortfolio
  }
}

export async function getPortfolioItemById(
  id: string
): Promise<PortfolioItem | undefined> {
  const items = await getPortfolioItems()
  return items.find((item) => item.id === id)
}

export async function getFeaturedPortfolioItems(): Promise<PortfolioItem[]> {
  try {
    return await fetchJson(`${API}/portfolio/featured`)
  } catch {
    return staticPortfolio.filter((item) => item.featured)
  }
}

export async function getTestimonials(): Promise<Testimonial[]> {
  try {
    return await fetchJson(`${API}/testimonials`)
  } catch {
    return staticTestimonials
  }
}

export async function getTeamMembers(): Promise<TeamMember[]> {
  try {
    return await fetchJson(`${API}/team`)
  } catch {
    return staticTeam
  }
}

export async function getBlogPosts(): Promise<BlogPost[]> {
  try {
    return await fetchJson(`${API}/blog?published=true`)
  } catch {
    return staticBlogPosts
  }
}

export async function getBlogPostBySlug(
  slug: string
): Promise<BlogPost | undefined> {
  try {
    return await fetchJson(`${API}/blog/slug/${slug}`)
  } catch {
    return staticBlogPosts.find((p) => p.slug === slug)
  }
}

export async function getStats(): Promise<Stat[]> {
  try {
    return await fetchJson(`${API}/stats`)
  } catch {
    return staticStats
  }
}

export async function getClients(): Promise<Client[]> {
  try {
    return await fetchJson(`${API}/clients`)
  } catch {
    return staticClients
  }
}

export async function getContactInfo(): Promise<ContactInfo> {
  try {
    const data: any = await fetchJson(`${API}/contact`)
    return {
      email: data.email || '',
      phone: data.phone || '',
      whatsapp: data.whatsapp || '',
      address: data.address || '',
      socials: data.socials || {},
    }
  } catch {
    return staticContact
  }
}
